import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdsTabsModule } from '@celerofinancas/design-system';
import { CompanyService } from '@celerofinancas/service-utils';
import { CompanyState, LoadActiveCoaAction, LoadUserAction } from '@celerofinancas/state-management';
import {
  BfmCashFlowFiltersComponent,
  BfmPaginatedCashFlowComponent,
  BfmPaginatedFinancialListComponent,
  ColumnItem,
} from '@celerofinancas/ui-cash-flow';
import { ModalService } from '@celerofinancas/ui-modals';
import { distinctUntilChanged, filter } from 'rxjs';

import { BankHeaderComponent } from '@shared/bank-header/bank-header.component';
import { FinancialListFiltersComponent } from './financial-list-filters/financial-list-filters.component';
import { FiltersComponent, FiltersModalResult, StatementFilters } from './filters/filters.component';
import { FailureWrapperComponent } from '@shared/failure-wrapper/failure-wrapper.component';
import { TokenExchange } from '@shared/models/token-exchange';

/**
 * Screen tabs. No "Boletos Previstos" — see the class docblock.
 */
type Tab = 'done' | 'all';

/**
 * Smart statement — card [86e2v9afr](https://app.clickup.com/t/86e2v9afr).
 *
 * Puts cash flow and the record list on a single screen, split by tab, as rule 3
 * of the card requires ("in the same format as Serasa").
 *
 * ## Two tabs, not three
 *
 * Serasa has a third tab, "Boletos Previstos", behind the `front_cashflow@dda`
 * feature flag. It stays out here because of rule 3: *"the predicted tab is not
 * present because at this first stage the GetNet project will have no predicted
 * records"*. Worth noting that Serasa's tab is about **boletos** (DDA), which
 * Getnet does not have — the dashboard still shows predicted values, which are
 * future settlement of a sale, a different thing.
 *
 * ## No download
 *
 * Rule 5: the Downloads module was not contracted. Export options are turned off
 * through token configuration (see `configs/tokens.ts`), not hidden with CSS — an
 * invisible button is still reachable by keyboard and still fires the request.
 */
@Component({
  selector: 'statement',
  standalone: true,
  imports: [
    CdsTabsModule,
    BfmCashFlowFiltersComponent,
    BfmPaginatedCashFlowComponent,
    BfmPaginatedFinancialListComponent,
    FinancialListFiltersComponent,
    BankHeaderComponent,
    FailureWrapperComponent,
  ],
  templateUrl: './statement.component.html',
  styleUrls: ['./statement.component.scss', './statement.overrides.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatementComponent extends TokenExchange implements OnInit {
  /**
   * Modal service, which opens the filters modal.
   */
  private readonly modalService = inject(ModalService);

  /**
   * Company service, the package's channel for the list parameters.
   */
  private readonly companyService = inject(CompanyService);

  /**
   * Destroy reference, which unsubscribes the parameter watchers with the component.
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Signal holding the current active tab.
   */
  private readonly selectedTab = signal<Tab>('done');

  /**
   * Filters applied on the "Todos os registros" tab.
   *
   * Starts empty on purpose. Serasa starts with a date window
   * (`getDateRangeAroundToday()`, ±15 days) because its database holds years of
   * records and opening everything would be heavy. Here the demo dataset has 49
   * records across 12 months: the window would hide ten of those months on first
   * open and give the impression that the base is almost empty.
   */
  private readonly filters = signal<StatementFilters | null>(null);

  /**
   * Search term — kept apart because it survives clearing the filters.
   */
  private readonly searchValue = signal('');

  constructor() {
    super();

    /**
     * Mirrors the applied parameters into the local signal.
     *
     * Without this the signal only knew about filters that **went through the
     * modal** — and the `bfm-active-filters` chips change the parameters on their
     * own (`removeFilter`/`clearFilters` call `setFinancialRecordParams` straight
     * on `CompanyService`). The result was measurable on screen: removing a chip
     * filtered the list correctly, but the modal reopened with the option still
     * selected and the next "Aplicar" resurrected the filter the user had just
     * removed.
     *
     * `distinctUntilChanged` by content because the service re-emits the same
     * object in situations that change nothing (`makeFinancialRecordRequest`
     * below being one of them).
     */
    this.companyService
      .onParamChanges()
      .pipe(
        distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((params: StatementFilters | null) => this.filters.set(params ?? null));
  }

  /**
   * Label for the detail column — the package renders an arbitrary template there.
   */
  @ViewChild('financialRecordDetail')
  public financialRecordDetailRef!: TemplateRef<unknown>;

  /**
   * Cash flow columns, in Serasa's order. `size` is a width proportion, not
   * pixels.
   */
  public readonly cashFlowColumnList: ColumnItem[] = [
    { name: 'payment_method', size: 2, align: 'center' },
    { name: 'paid_at', size: 1, align: 'left' },
    { name: 'name', size: 2, align: 'left' },
    { name: 'coa_name', size: 2, align: 'left' },
    { name: 'bank_id', size: 1, align: 'center' },
    { name: 'value', size: 2, align: 'right' },
    { name: 'custom', size: 2, align: 'right', template: () => this.financialRecordDetailRef },
  ];

  /**
   * Record list columns — status instead of payment method.
   *
   * ## The sizes add up to 12, and that is what closes the right edge
   *
   * The package lays both lists out on a grid of **12** columns. This one used to add up
   * to 11, so the twelfth was left empty at the right end: measured on screen at 1440px,
   * the header ended 110px short of its own right edge while the left one stopped at
   * 16px, and the "Realizados" tab, whose list does add up to 12, closed at 16px on both
   * sides. That is item 3+15 of the statement document
   * ([86e33ffqj](https://app.clickup.com/t/86e33ffqj)): the two sides unequal, and the
   * two tabs disagreeing.
   *
   * The spare unit goes to the **last** column, which is what makes this tab behave like
   * "Realizados": the last column reaches the right edge and the "Ver" link sits 16px
   * from it, as the header label above it does. Giving it to any other column would
   * close the edge too and move every column after it — a decision about widths that no
   * card makes.
   *
   * Recorded for the design review: "Nome" still truncates on the longer records
   * ("Segunda leva de venda…"). It is the column the spare unit would help, and widening
   * it is one character here.
   */
  public readonly financialRecordColumnList: ColumnItem[] = [
    { name: 'status', size: 1, align: 'center' },
    { name: 'name', size: 2, align: 'left' },
    { name: 'coa_name', size: 2, align: 'left' },
    { name: 'bank_id', size: 1, align: 'center' },
    { name: 'paid_at', size: 1, align: 'left' },
    { name: 'value', size: 2, align: 'right' },
    { name: 'custom', size: 3, align: 'right', template: () => this.financialRecordDetailRef },
  ];

  /**
   * Verify tab is selected.
   *
   * @param tab tab to compare against the active one
   * @returns whether the given tab is the active one
   */
  public isSelectedTab(tab: Tab): boolean {
    return this.selectedTab() === tab;
  }

  /**
   * Change selected tab.
   *
   * @param tab tab to activate
   */
  public setSelectedTab(tab: Tab): void {
    this.selectedTab.set(tab);
  }

  /**
   * Same reason as the dashboard and the reports — see `DashboardComponent`.
   */
  /**
   * Loads the user, for the same reason as the dashboard and the reports — see
   * `DashboardComponent` — and warms up the active chart of accounts.
   *
   * ## Why the chart of accounts is loaded here
   *
   * The "Classificação" field of the detail modal renders `cds-coa-autocomplete` with
   * `activeCoaPk` read from `ActiveCoaState`. Nothing in the modal's own chain loads
   * it: `ActiveCoaState` is only hydrated by an explicit `LoadActiveCoaAction`, so
   * opening a record from a cold state sent the autocomplete an empty `pk`, the API
   * answered 400 `'pk should not be empty'` and the screen showed the toast *"Empresa
   * não possui plano de contas cadastrado!"* — with a plan that does exist.
   *
   * The filters modal already dispatched it, which is why the field worked after
   * opening the filters and failed before. Loading it with the screen removes the
   * ordering dependency.
   *
   * It waits for the company because the action takes its pk, and `CompanyState` is
   * hydrated asynchronously by `CoreRegistryInterceptor` reacting to the first
   * request — at `ngOnInit` it is still empty.
   */
  public ngOnInit(): void {
    this.store.dispatch(new LoadUserAction());

    this.store
      .select(CompanyState.getActiveCompanyPk)
      .pipe(
        filter((companyPk): companyPk is string => !!companyPk),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((companyPk) => this.store.dispatch(new LoadActiveCoaAction(companyPk)));
  }

  /**
   * Search by name. Keeps the filters already applied — typing a name is not a
   * request to drop the period the user chose.
   *
   * @param searchTerm term confirmed in the search field
   */
  public onSubmitSearch(searchTerm: string): void {
    this.searchValue.set(searchTerm);
    this.makeFinancialRecordRequest({ ...(this.filters() ?? {}), search: searchTerm });
  }

  /**
   * Opens the modal and applies whatever comes back.
   *
   * Three distinct outcomes, which is why the payload is `{ filters }` instead of
   * the object alone: `undefined` (closed on the backdrop, nothing changes),
   * `null` ("Limpar filtros") and the filled object ("Aplicar filtros"). Without
   * the envelope there would be no way to tell "cancelled" from "cleared".
   */
  public openFilters(): void {
    const reference = this.modalService.open<
      FiltersModalResult,
      { headerTitle: string; framed: boolean; filters: StatementFilters }
    >(
      FiltersComponent,
      // `framed: true` turns on the card and header of `ModalWrapperComponent` —
      // the `ui-cash-flow` modals do not pass the flag because they draw their own.
      { headerTitle: 'Selecione os filtros', framed: true, filters: this.filters() ?? {} },
      { modal: { closeStrategy: 'backdropClick' } },
    );

    reference.afterClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ data }) => {
      if (typeof data?.filters === 'undefined') {
        return;
      }

      this.filters.set(data.filters);
      this.makeFinancialRecordRequest({ ...(data.filters ?? {}), search: this.searchValue() });
    });
  }

  /**
   * Re-runs the list request with the given filters.
   *
   * `setFinancialRecordParams` **replaces** the whole parameter object (it does
   * not merge), so whatever is not here stops applying — which is exactly what we
   * want when applying a new set of filters. The sequence is the same one
   * `bfm-paginated-financial-list` runs internally when it receives
   * `initialFilters`: back to page 1, empty the accumulated list (infinite
   * scrolling) and only then ask again.
   *
   * @param filters full set of filters to apply, optionally carrying the search term
   */
  private makeFinancialRecordRequest(filters: StatementFilters & { search?: string }): void {
    this.companyService.setFinancialRecordParams(filters);
    this.companyService.setPage(1);
    this.companyService.clearFinancialRecordsList();
    this.companyService.setStatement();
    this.companyService.setColumnsList(this.financialRecordColumnList);
    this.companyService.setGroupBy('none');
    this.companyService.applyPreferences();
  }
}
